import { WorldPoint } from '../domain/types.ts';

export class Camera {
  public worldX = 0; // meters from center
  public worldY = 0; // meters from center
  public zoom = 1.0; // scale factor
  public maxRadiusM = 100; // Co giãn bán kính lên đến 200 mét tùy theo bản đồ

  private screenWidth = 0;
  private screenHeight = 0;
  public baseZoom = 1.0;
  public minZoom = 0.35;
  public maxZoom = 6.0;

  public setMaxRadius(radius: number): void {
    this.maxRadiusM = Math.max(80, Math.min(250, radius));
    this.recalculateBaseZoom();
  }

  public resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.recalculateBaseZoom();

    // Nếu chưa zoom hoặc ở mức khởi đầu, đặt zoom về đúng baseZoom
    if (this.zoom < this.minZoom || this.zoom > this.maxZoom || this.zoom === 1.0) {
      this.zoom = this.baseZoom;
    }
  }

  private recalculateBaseZoom(): void {
    if (this.screenWidth <= 0 || this.screenHeight <= 0) return;
    const minDim = Math.min(this.screenWidth, this.screenHeight);
    this.baseZoom = (minDim * 0.86) / (this.maxRadiusM * 2);
    this.minZoom = this.baseZoom * 0.7;
    this.maxZoom = this.baseZoom * 5.0;
  }

  public resetZoom(): void {
    this.recalculateBaseZoom();
    this.zoom = this.baseZoom;
    this.worldX = 0;
    this.worldY = 0;
  }

  public zoomBy(factor: number, screenCenterX?: number, screenCenterY?: number): void {
    const prevZoom = this.zoom;
    const targetZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * factor));
    if (Math.abs(targetZoom - prevZoom) < 0.001) return;

    // Zoom hướng về tâm con trỏ hoặc tâm màn hình
    const cx = screenCenterX ?? this.screenWidth / 2;
    const cy = screenCenterY ?? this.screenHeight / 2;

    const worldBefore = this.screenToWorld(cx, cy);
    this.zoom = targetZoom;
    const worldAfter = this.screenToWorld(cx, cy);

    // Bù trừ world position để điểm zoom cố định dưới con trỏ / tâm màn hình
    this.worldX += worldBefore.x - worldAfter.x;
    this.worldY += worldBefore.y - worldAfter.y;
    this.clampPan();
  }

  public zoomIn(factor = 1.3): void {
    this.zoomBy(factor);
  }

  public zoomOut(factor = 1.3): void {
    this.zoomBy(1 / factor);
  }

  public pan(deltaScreenX: number, deltaScreenY: number): void {
    const deltaWorldX = deltaScreenX / this.zoom;
    const deltaWorldY = deltaScreenY / this.zoom;

    this.worldX -= deltaWorldX;
    this.worldY -= deltaWorldY;
    this.clampPan();
  }

  /**
   * Camera Soft-Deadzone: Khi nhân vật ở trong vùng deadzone bán kính R (pixel),
   * Camera KHÔNG DI CHUYỂN, giúp người chơi thấy rõ nhân vật & con chó chạy qua lại trên màn hình!
   */
  public followWithDeadzone(
    targetX: number,
    targetY: number,
    deadzoneScreenRadius = 75,
    smoothFactor = 0.08
  ): void {
    const dxScreen = (targetX - this.worldX) * this.zoom;
    const dyScreen = (targetY - this.worldY) * this.zoom;
    const distScreen = Math.hypot(dxScreen, dyScreen);

    if (distScreen <= deadzoneScreenRadius) {
      return;
    }

    const excessDist = distScreen - deadzoneScreenRadius;
    const excessRatio = excessDist / distScreen;
    const shiftWorldX = ((dxScreen * excessRatio) / this.zoom) * smoothFactor;
    const shiftWorldY = ((dyScreen * excessRatio) / this.zoom) * smoothFactor;

    this.worldX += shiftWorldX;
    this.worldY += shiftWorldY;
    this.clampPan();
  }

  public follow(targetX: number, targetY: number, smoothFactor = 0.08): void {
    // Tự động scale deadzone: chiếm ~30% cạnh nhỏ nhất của màn hình
    const deadzoneRadius = Math.max(80, Math.min(this.screenWidth, this.screenHeight) * 0.3);
    this.followWithDeadzone(targetX, targetY, deadzoneRadius, smoothFactor);
  }

  private clampPan(): void {
    const dist = Math.hypot(this.worldX, this.worldY);
    if (dist > this.maxRadiusM) {
      const angle = Math.atan2(this.worldY, this.worldX);
      this.worldX = Math.cos(angle) * this.maxRadiusM;
      this.worldY = Math.sin(angle) * this.maxRadiusM;
    }
  }

  public worldToScreen(worldPoint: WorldPoint): { x: number; y: number } {
    const relX = worldPoint.x - this.worldX;
    const relY = worldPoint.y - this.worldY;

    return {
      x: this.screenWidth / 2 + relX * this.zoom,
      y: this.screenHeight / 2 + relY * this.zoom,
    };
  }

  public screenToWorld(screenX: number, screenY: number): WorldPoint {
    const relX = screenX - this.screenWidth / 2;
    const relY = screenY - this.screenHeight / 2;

    return {
      x: this.worldX + relX / this.zoom,
      y: this.worldY + relY / this.zoom,
    };
  }
}
